import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useHistory } from 'react-router-dom';
import Loader from '../shared/displayComponents/loader/Loader';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../messages/ThreadQuill.css';
import url from '../constants/config';
import {
  fetchChecklist,
  updateTaskAdmin,
  updateTaskTextValue,
  assignTaskToAssistant,
  fetchAssistants,
  fetchFullAccessAssistants,
  grantFullAccess,
  revokeFullAccess,
  addCustomTask,
  deleteCustomTask,
  uploadAttachment,
  deleteAttachment,
  addAdminLink,
  deleteAdminLink,
  saveAdminNote,
  markTaskRead,
  setNeedsTemplateAssignment,
  setStep2Unlocked,
  setStep3Unlocked,
  setPendingNotesTaskId,
} from './store/checklistSlice';
import { toasterOn } from '../shared/store/actions';
import TaskCommentPanel from './TaskCommentPanel';
import TaskNotesPanel from './TaskNotesPanel';
import FileUploadModal from './FileUploadModal';
import ChecklistLinkDate from './ChecklistLinkDate';

const displayLinkHost = (href) => {
  try {
    const u = new URL(href);
    return (u.hostname + (u.pathname.length > 1 ? u.pathname : '')).slice(0, 50);
  } catch {
    return href.slice(0, 50);
  }
};

const STEP_TITLES = {
  1: 'START REKRUTACJI',
  2: 'PO AKCEPTACJI UCZELNI',
  3: 'PRZYGOTOWANIE DO WYLOTU',
};

const STATUS_LABELS = {
  not_started:        'Do rozpoczęcia',
  in_progress:        'W realizacji',
  waiting_athlete:    'Oczekuje na sportowca',
  waiting_university: 'Oczekuje na uczelnię',
  waiting_playsport:  'Oczekuje na PLAYSPORTUSA',
  to_verify:          'Do weryfikacji',
  on_hold:            'Wstrzymane',
  done:               'Zrealizowane',
  // backward compat
  waiting:            'Oczekiwanie',
};

const STATUS_FILTER_OPTS = [
  { val: null,                lbl: 'Wszystkie' },
  { val: 'not_started',       lbl: 'Do rozp.' },
  { val: 'in_progress',       lbl: 'W realizacji' },
  { val: 'waiting_athlete',   lbl: 'Oczek. sportowiec' },
  { val: 'waiting_university',lbl: 'Oczek. uczelnia' },
  { val: 'waiting_playsport', lbl: 'Oczek. PSA' },
  { val: 'to_verify',         lbl: 'Do weryfikacji' },
  { val: 'on_hold',           lbl: 'Wstrzymane' },
  { val: 'done',              lbl: 'Zrealizowane' },
];

const noteQuillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const formatDeadline = (deadline) => {
  if (!deadline) return '';
  return new Date(deadline).toISOString().slice(0, 10);
};

const getAttachmentDownloadUrl = (playerId, taskId, attachmentId) =>
  `${url}/checklist/${playerId}/tasks/${taskId}/attachments/${attachmentId}`;

const openAttachment = async (fileUrl, filename) => {
  try {
    const token = window.localStorage.getItem('authorization');
    const response = await fetch(fileUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch attachment');
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const newTab = window.open(blobUrl, '_blank');
    if (!newTab) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to open attachment:', err);
  }
};

const PRIORITY_OPTIONS = [
  { value: '', label: 'Brak priorytetu' },
  { value: 'high', label: 'Wysoki' },
  { value: 'medium', label: 'Normalny' },
  { value: 'low', label: 'Niski' },
];

const TASK_OWNER_OPTIONS = [
  { value: '', label: 'Brak' },
  { value: 'athlete', label: 'Zawodnik' },
  { value: 'admin', label: 'Agencja' },
];


const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const AdminChecklist = ({ playerId }) => {
  const dispatch = useDispatch();
  const { items, adminNote, loading, needsTemplateAssignment, step2Unlocked, step3Unlocked, assistants, fullAccessAssistants, pendingNotesTaskId } = useSelector(({ checklist }) => checklist);
  const userRole = useSelector(({ authorization }) => authorization.userRole);
  const currentUser = useSelector(({ authorization }) => authorization.user);
  const [assignModalTask, setAssignModalTask] = useState(null);
  const [fullAccessDropdown, setFullAccessDropdown] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [assignTemplateId, setAssignTemplateId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const [noteContent, setNoteContent] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newTaskLabel, setNewTaskLabel] = useState({ 1: '', 2: '', 3: '' });
  const [newTaskDeadline, setNewTaskDeadline] = useState({ 1: '', 2: '', 3: '' });
  const [newTaskAthleteVisible, setNewTaskAthleteVisible] = useState({ 1: true, 2: true, 3: true });
  const [newTaskPriority, setNewTaskPriority] = useState({ 1: '', 2: '', 3: '' });
  const [newTaskOwner, setNewTaskOwner] = useState({ 1: '', 2: '', 3: '' });
  const [newLinkInputs, setNewLinkInputs] = useState({});
  const [textValueDrafts, setTextValueDrafts] = useState({});
  const [filterStep, setFilterStep] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [newTaskRequires, setNewTaskRequires] = useState({ 1: [], 2: [], 3: [] });
  const [uploadModalTaskId, setUploadModalTaskId] = useState(null);
  const [editingLabelTaskId, setEditingLabelTaskId] = useState(null);
  const [labelDrafts, setLabelDrafts] = useState({});
  const [notesTask, setNotesTask] = useState(null);

  useEffect(() => {
    if (playerId) {
      dispatch(fetchChecklist(playerId));
      if (userRole === 'admin') {
        dispatch(fetchAssistants());
        dispatch(fetchFullAccessAssistants(playerId));
      }
    }
  }, [dispatch, playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setNoteContent(adminNote || '');
  }, [adminNote]);

  // Keep selectedTask panel in sync when Redux items update
  useEffect(() => {
    if (selectedTask) {
      const updated = items.find(i => i._id === selectedTask._id);
      if (updated) setSelectedTask(updated);
    }
    if (notesTask) {
      const updated = items.find(i => i._id === notesTask._id);
      if (updated) setNotesTask(updated);
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch available templates when player has no assignment
  useEffect(() => {
    if (!needsTemplateAssignment) return;
    axios.get(`${url}/checklist-templates`, { headers: getAuthHeader() })
      .then(res => {
        const list = res.data.templates || [];
        setAvailableTemplates(list);
        if (list.length > 0) setAssignTemplateId(list[0]._id);
      })
      .catch(() => {});
  }, [needsTemplateAssignment]);

  // Auto-open task from ?task= query param (e.g. clicked from notification)
  useEffect(() => {
    if (!items.length) return;
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    if (taskId) {
      const task = items.find(i => i._id === taskId);
      if (task) {
        setSelectedTask(task);
        dispatch(markTaskRead(playerId, taskId));
      }
    }
  }, [items, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open notes panel when triggered from notification bell
  useEffect(() => {
    if (!pendingNotesTaskId || !items.length) return;
    const task = items.find(i => i._id === pendingNotesTaskId);
    if (task) {
      setNotesTask(task);
      setSelectedTask(null);
      dispatch(setPendingNotesTaskId(null));
    }
  }, [pendingNotesTaskId, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveNote = async () => {
    setNoteSaving(true);
    await dispatch(saveAdminNote(playerId, noteContent));
    setNoteSaving(false);
  };

  const handleAddTask = async (step) => {
    const label = newTaskLabel[step].trim();
    if (!label) return;
    await dispatch(
      addCustomTask(
        playerId,
        step,
        label,
        newTaskDeadline[step] || undefined,
        newTaskAthleteVisible[step],
        newTaskPriority[step] || undefined,
        newTaskOwner[step] || undefined,
        undefined,
        newTaskRequires[step].length > 0 ? newTaskRequires[step] : undefined
      )
    );
    setNewTaskLabel(prev => ({ ...prev, [step]: '' }));
    setNewTaskDeadline(prev => ({ ...prev, [step]: '' }));
    setNewTaskAthleteVisible(prev => ({ ...prev, [step]: true }));
    setNewTaskPriority(prev => ({ ...prev, [step]: '' }));
    setNewTaskOwner(prev => ({ ...prev, [step]: '' }));
    setNewTaskRequires(prev => ({ ...prev, [step]: [] }));
  };

  const handleAddLink = (taskId) => {
    const linkUrl = (newLinkInputs[taskId] || '').trim();
    if (!linkUrl) return;
    dispatch(addAdminLink(playerId, taskId, linkUrl));
    setNewLinkInputs(prev => ({ ...prev, [taskId]: '' }));
  };

  const startLabelEdit = (task) => {
    setEditingLabelTaskId(task._id);
    setLabelDrafts(prev => ({ ...prev, [task._id]: task.label || '' }));
  };

  const cancelLabelEdit = (taskId) => {
    setEditingLabelTaskId(null);
    setLabelDrafts(prev => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  const saveLabelEdit = (task) => {
    const draft = (labelDrafts[task._id] ?? task.label ?? '').trim();
    if (!draft) return;
    if (draft !== task.label) {
      dispatch(updateTaskAdmin(playerId, task._id, { label: draft }));
    }
    cancelLabelEdit(task._id);
  };

  const handleAssignTemplate = async () => {
    if (!assignTemplateId) return;
    setAssigning(true);
    try {
      await axios.post(
        `${url}/checklist-templates/${assignTemplateId}/apply`,
        { playerIds: [playerId] },
        { headers: getAuthHeader() }
      );
      dispatch(setNeedsTemplateAssignment(false));
      dispatch(fetchChecklist(playerId));
    } catch {
      // silently fail — checklist reload will show updated state
    } finally {
      setAssigning(false);
    }
  };

  const handleToggleStep = async (step, unlock) => {
    if (unlocking) return;
    setUnlocking(true);
    try {
      const res = await axios.patch(
        `${url}/admin/players/${playerId}/unlock-steps`,
        { step, unlock },
        { headers: getAuthHeader() }
      );
      dispatch(setStep2Unlocked(res.data.step2Unlocked === true));
      dispatch(setStep3Unlocked(res.data.step3Unlocked === true));
    } catch (err) {
      const message = err?.response?.data?.message || 'Nie udało się zmienić widoczności kroku';
      dispatch(toasterOn({ message, type: 'failed' }));
    } finally {
      setUnlocking(false);
    }
  };

  const steps = [1, 2, 3];
  const singleFullAccessAssistantEmail = fullAccessAssistants.length === 1
    ? fullAccessAssistants[0].assistantEmail
    : null;
  const fullAccessAssistantEmails = fullAccessAssistants.map(a => a.assistantEmail);
  const taskExcludedFromSingleFullAccess = Boolean(
    assignModalTask
    && singleFullAccessAssistantEmail
    && Array.isArray(assignModalTask.excludedAssistantEmails)
    && assignModalTask.excludedAssistantEmails.includes(singleFullAccessAssistantEmail)
  );

  const getAssistantDisplayName = (assistantEmail) => {
    const fromAssistants = assistants.find(a => a.universityEmail === assistantEmail);
    if (fromAssistants) {
      return fromAssistants.firstName || fromAssistants.universityEmail;
    }
    const fromFullAccess = fullAccessAssistants.find(a => a.assistantEmail === assistantEmail);
    if (fromFullAccess) {
      return fromFullAccess.firstName || fromFullAccess.assistantEmail;
    }
    if (currentUser && currentUser.universityEmail === assistantEmail && currentUser.firstName) {
      return `${currentUser.firstName}${currentUser.lastName ? ' ' + currentUser.lastName : ''}`;
    }
    return assistantEmail;
  };

  const getEffectiveAssistantEmailsForTask = (item) => {
    if (item.assignedToAssistant) return [item.assignedToAssistant];
    if (item.adminOnly) return [];
    const excluded = Array.isArray(item.excludedAssistantEmails) ? item.excludedAssistantEmails : [];
    return fullAccessAssistantEmails.filter(email => !excluded.includes(email));
  };

  const selectCtrlStyle = {
    fontSize: '12px', padding: '3px 8px', border: '1px solid #cbd5e1',
    borderRadius: '4px', background: '#f8fafc', cursor: 'pointer',
  };

  return (
    <div style={{ marginTop: '40px', borderTop: '2px solid #e2e8f0', paddingTop: '32px' }}>
      {/* Header + template link */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ color: '#1e293b', fontWeight: '700', margin: 0, fontSize: '20px' }}>
          <i className="fas fa-tasks" style={{ marginRight: '8px', color: '#2e496b' }} />
          Checklist rekrutacyjny
        </h3>
        {userRole !== 'assistant' && (
          <Link
            to="/admin/checklist-template"
            style={{ fontSize: '13px', color: '#2e496b', textDecoration: 'none', border: '1px solid #2e496b', borderRadius: '6px', padding: '5px 14px' }}
          >
            <i className="fas fa-layer-group" style={{ marginRight: '6px' }} />
            Zarządzaj szablonem
          </Link>
        )}
      </div>

      {/* Admin note section — hidden for assistant */}
      {userRole !== 'assistant' && <div style={{ marginBottom: '24px' }}>
        <h4 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>
          <i className="fas fa-sticky-note" style={{ marginRight: '8px', color: '#2e496b' }} />
          Notatka wewnętrzna (tylko admin)
        </h4>
        <div className="thread-quill" style={{ marginBottom: '10px' }}>
          <ReactQuill
            value={noteContent}
            onChange={setNoteContent}
            modules={noteQuillModules}
            theme="snow"
            placeholder="Wpisz notatki dotyczące rekrutacji zawodnika..."
          />
        </div>
        <button
          type="button"
          onClick={handleSaveNote}
          disabled={noteSaving}
          style={{
            background: '#2e496b',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 20px',
            fontSize: '14px',
            cursor: noteSaving ? 'not-allowed' : 'pointer',
            opacity: noteSaving ? 0.7 : 1,
          }}
        >
          {noteSaving ? 'Zapisywanie...' : (
            <><i className="fas fa-save" style={{ marginRight: '6px' }} />Zapisz notatkę</>
          )}
        </button>
      </div>}

      {/* Full access section — admin only */}
      {userRole === 'admin' && (
        <div style={{ marginBottom: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
          <h4 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '12px', fontSize: '16px' }}>
            <i className="fas fa-user-shield" style={{ marginRight: '8px', color: '#7c3aed' }} />
            Pełny dostęp asystentki
          </h4>
          <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
            Asystentka z pełnym dostępem widzi wszystkie zadania tego zawodnika (oprócz „Tylko Admin").
          </p>

          {/* Current full-access assistants */}
          {fullAccessAssistants.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {fullAccessAssistants.map(a => {
                const fullName = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.assistantEmail;
                return (
                  <span
                    key={a.assistantEmail}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      background: '#f5f3ff', border: '1px solid #ddd6fe',
                      borderRadius: '20px', padding: '4px 12px', fontSize: '13px', color: '#6d28d9',
                    }}
                  >
                    <i className="fas fa-user-shield" style={{ fontSize: '11px' }} />
                    {fullName}
                    <button
                      type="button"
                      onClick={() => dispatch(revokeFullAccess(playerId, a.assistantEmail))}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#a78bfa', fontSize: '12px', padding: '0 0 0 2px', lineHeight: 1,
                      }}
                      title="Cofnij pełny dostęp"
                    >
                      <i className="fas fa-times" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Grant full access */}
          {assistants.filter(a => !fullAccessAssistants.some(f => f.assistantEmail === a.universityEmail)).length > 0 && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={fullAccessDropdown}
                onChange={e => setFullAccessDropdown(e.target.value)}
                style={{
                  fontSize: '13px', padding: '6px 10px',
                  border: '1px solid #ddd6fe', borderRadius: '6px',
                  minWidth: '200px', background: '#faf5ff', color: '#3b0764',
                }}
              >
                <option value="">Wybierz asystentkę...</option>
                {assistants
                  .filter(a => !fullAccessAssistants.some(f => f.assistantEmail === a.universityEmail))
                  .map(a => {
                    const name = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.universityEmail;
                    return (
                      <option key={a.universityEmail} value={a.universityEmail}>{name}</option>
                    );
                  })
                }
              </select>
              <button
                type="button"
                disabled={!fullAccessDropdown}
                onClick={() => {
                  if (!fullAccessDropdown) return;
                  dispatch(grantFullAccess(playerId, fullAccessDropdown));
                  setFullAccessDropdown('');
                }}
                style={{
                  background: fullAccessDropdown ? '#7c3aed' : '#e2e8f0',
                  color: fullAccessDropdown ? '#fff' : '#94a3b8',
                  border: 'none', borderRadius: '6px',
                  padding: '7px 16px', fontSize: '13px',
                  cursor: fullAccessDropdown ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                }}
              >
                <i className="fas fa-unlock" style={{ marginRight: '6px' }} />
                Przyznaj pełny dostęp
              </button>
            </div>
          )}

          {assistants.length === 0 && (
            <p style={{ fontSize: '13px', color: '#94a3b8' }}>Brak asystentek w systemie.</p>
          )}
        </div>
      )}

      {/* Filter controls */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Krok:</span>
        {[null, 1, 2, 3].map(s => (
          <button
            key={String(s)}
            type="button"
            onClick={() => setFilterStep(s)}
            style={{
              fontSize: '12px', padding: '3px 12px', borderRadius: '4px', cursor: 'pointer',
              border: filterStep === s ? '1.5px solid #2e496b' : '1px solid #cbd5e1',
              background: filterStep === s ? '#2e496b' : '#f8fafc',
              color: filterStep === s ? '#fff' : '#475569',
              fontWeight: filterStep === s ? '600' : '400',
            }}
          >
            {s === null ? 'Wszystkie' : `Krok ${s}`}
          </button>
        ))}
        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', marginLeft: '8px' }}>Status:</span>
        {STATUS_FILTER_OPTS.map(({ val, lbl }) => (
          <button
            key={String(val)}
            type="button"
            onClick={() => setFilterStatus(val)}
            style={{
              fontSize: '12px', padding: '3px 12px', borderRadius: '4px', cursor: 'pointer',
              border: filterStatus === val ? '1.5px solid #2e496b' : '1px solid #cbd5e1',
              background: filterStatus === val ? '#2e496b' : '#f8fafc',
              color: filterStatus === val ? '#fff' : '#475569',
              fontWeight: filterStatus === val ? '600' : '400',
            }}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* Step lock/unlock controls — shown when player has items (admin only) */}
      {!loading && items.length > 0 && userRole !== 'assistant' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: !step2Unlocked || !step3Unlocked ? '#fefce8' : '#ecfdf5',
            border: !step2Unlocked || !step3Unlocked ? '1px solid #fde047' : '1px solid #6ee7b7',
            borderRadius: '8px',
            padding: '12px 18px',
            marginBottom: '16px',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <i
              className={`fas fa-${!step2Unlocked || !step3Unlocked ? 'lock' : 'unlock'}`}
              style={{ color: !step2Unlocked || !step3Unlocked ? '#ca8a04' : '#059669', fontSize: '16px' }}
            />
            <span style={{ fontSize: '13px', color: !step2Unlocked || !step3Unlocked ? '#78350f' : '#065f46', fontWeight: '500' }}>
              {!step2Unlocked && !step3Unlocked
                ? 'Krok 2 i Krok 3 są zablokowane dla tego zawodnika.'
                : !step2Unlocked
                ? 'Krok 2 jest zablokowany dla tego zawodnika.'
                : !step3Unlocked
                ? 'Krok 3 jest zablokowany dla tego zawodnika.'
                : 'Krok 2 i Krok 3 są odblokowane dla tego zawodnika.'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => handleToggleStep(2, !step2Unlocked)}
              disabled={unlocking}
              style={{
                background: step2Unlocked ? '#dc2626' : '#ca8a04',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '7px 18px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: unlocking ? 'not-allowed' : 'pointer',
                opacity: unlocking ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              <i className={`fas fa-${step2Unlocked ? 'lock' : 'unlock'}`} style={{ marginRight: '6px' }} />
              {step2Unlocked ? 'Zablokuj Krok 2' : 'Odblokuj Krok 2'}
            </button>
            <button
              type="button"
              onClick={() => handleToggleStep(3, !step3Unlocked)}
              disabled={unlocking}
              style={{
                background: step3Unlocked ? '#dc2626' : '#ca8a04',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '7px 18px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: unlocking ? 'not-allowed' : 'pointer',
                opacity: unlocking ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              <i className={`fas fa-${step3Unlocked ? 'lock' : 'unlock'}`} style={{ marginRight: '6px' }} />
              {step3Unlocked ? 'Zablokuj Krok 3' : 'Odblokuj Krok 3'}
            </button>
          </div>
        </div>
      )}

      {/* Template assignment picker — admin only */}
      {!loading && needsTemplateAssignment && items.length === 0 && userRole !== 'assistant' && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '24px', marginBottom: '24px', textAlign: 'center' }}>
          <i className="fas fa-layer-group" style={{ fontSize: '28px', color: '#2e496b', marginBottom: '12px', display: 'block' }} />
          <h4 style={{ color: '#1e293b', fontWeight: '600', marginBottom: '8px', fontSize: '16px' }}>
            Brak szablonu dla tego zawodnika
          </h4>
          <p style={{ color: '#475569', fontSize: '14px', marginBottom: '20px' }}>
            Wybierz szablon checklisty i przypisz go, aby zasiać zadania dla tego zawodnika.
          </p>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <select
              value={assignTemplateId}
              onChange={e => setAssignTemplateId(e.target.value)}
              style={{ fontSize: '14px', padding: '7px 12px', border: '1px solid #2e496b', borderRadius: '6px', minWidth: '220px' }}
            >
              {availableTemplates.map(t => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAssignTemplate}
              disabled={!assignTemplateId || assigning}
              style={{
                background: '#2e496b', color: '#fff', border: 'none', borderRadius: '6px',
                padding: '8px 20px', fontSize: '14px',
                cursor: assignTemplateId && !assigning ? 'pointer' : 'not-allowed',
                opacity: assignTemplateId && !assigning ? 1 : 0.5,
              }}
            >
              <i className="fas fa-play-circle" style={{ marginRight: '7px' }} />
              {assigning ? 'Przypisywanie...' : 'Przypisz i uruchom'}
            </button>
          </div>
        </div>
      )}

      {!loading && userRole === 'assistant' && items.length === 0 && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '32px', textAlign: 'center', marginBottom: '24px' }}>
          <i className="fas fa-user-shield" style={{ fontSize: '28px', color: '#94a3b8', marginBottom: '12px', display: 'block' }} />
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            Admin nie przydzielił jeszcze żadnych zadań dla tego sportowca.
          </p>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        steps.filter(s => filterStep === null || s === filterStep).filter(s => {
          if (userRole !== 'assistant') return true;
          return items.filter(i => i.step === s).length > 0;
        }).map(step => {
          const stepItems = items
            .filter(i => i.step === step)
            .filter(i => filterStatus === null || i.status === filterStatus);
          const doneCount = stepItems.filter(i => i.status === 'done').length;

          return (
            <div key={step} style={{ marginBottom: '32px' }}>
              {/* Step header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#2e496b',
                  color: '#fff',
                  padding: '10px 16px',
                  borderRadius: '8px 8px 0 0',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.05em',
                }}
              >
                <span>Krok {step}: {STEP_TITLES[step]}</span>
                <span style={{ fontSize: '12px', opacity: 0.85 }}>
                  {doneCount} / {stepItems.length} ukończono
                </span>
              </div>

              {/* Task rows */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  overflow: 'hidden',
                }}
              >
                {stepItems.map((item, idx) => {
                  const hasAttachments = Array.isArray(item.attachments) && item.attachments.length > 0;
                  const hasLinks = Array.isArray(item.links) && item.links.length > 0;
                  const req = Array.isArray(item.requires) ? item.requires : [];
                  const showFilesSection = req.includes('file') || hasAttachments;
                  const showLinksSection = req.includes('link') || hasLinks;
                  const effectiveAssistantEmails = getEffectiveAssistantEmailsForTask(item);
                  const hasAssistantAssignmentVisual = effectiveAssistantEmails.length > 0;

                  return (
                  <div
                    key={item._id}
                    style={{
                      padding: '14px 16px',
                      borderBottom: idx < stepItems.length - 1 ? '1px solid #e2e8f0' : 'none',
                      background: item.athleteVisible === false
                        ? '#fef9f0'
                        : item.status === 'done' ? '#f0fdf4' : '#fff',
                    }}
                  >
                    {/* === ROW 1: Task label + inline badges === */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                      {/* Status dot */}
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          marginTop: '5px',
                          background:
                            item.status === 'done'
                              ? '#22c55e'
                              : item.status === 'in_progress'
                              ? '#f59e0b'
                              : item.status === 'waiting'
                              ? '#60a5fa'
                              : '#cbd5e1',
                          flexShrink: 0,
                        }}
                      />

                      {/* Label + badges */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          {editingLabelTaskId === item._id ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              <input
                                type="text"
                                value={labelDrafts[item._id] ?? ''}
                                onChange={e => {
                                  const val = e.target.value;
                                  setLabelDrafts(prev => ({ ...prev, [item._id]: val }));
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveLabelEdit(item);
                                  if (e.key === 'Escape') cancelLabelEdit(item._id);
                                }}
                                autoFocus
                                style={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  padding: '3px 7px',
                                  border: '1px solid #94a3b8',
                                  borderRadius: '4px',
                                  minWidth: '220px',
                                  color: '#1e293b',
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => saveLabelEdit(item)}
                                style={{
                                  background: '#16a34a',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  padding: '3px 7px',
                                }}
                                title="Zapisz nazwę zadania"
                              >
                                <i className="fas fa-check" />
                              </button>
                              <button
                                type="button"
                                onClick={() => cancelLabelEdit(item._id)}
                                style={{
                                  background: '#f1f5f9',
                                  color: '#64748b',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  padding: '3px 7px',
                                }}
                                title="Anuluj"
                              >
                                <i className="fas fa-times" />
                              </button>
                            </span>
                          ) : (
                            <>
                              <span
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 600,
                                  color: item.status === 'done' ? '#15803d' : '#1e293b',
                                  textDecoration: item.status === 'done' ? 'line-through' : 'none',
                                }}
                              >
                                {item.label}
                              </span>
                              {userRole === 'admin' && (
                                <button
                                  type="button"
                                  onClick={() => startLabelEdit(item)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    padding: '1px 4px',
                                  }}
                                  title="Edytuj nazwę zadania"
                                >
                                  <i className="fas fa-pen" />
                                </button>
                              )}
                            </>
                          )}
                          {!item.isDefault && (
                            <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', borderRadius: '4px', padding: '1px 5px' }}>
                              własne
                            </span>
                          )}
                          {item.athleteVisible === false && !item.adminOnly && (
                            <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 6px' }} title="Niewidoczne dla zawodnika">
                              <span role="img" aria-label="ukryte">🔒</span> Admin
                            </span>
                          )}
                          {item.adminOnly && (
                            <span style={{ fontSize: '10px', background: '#fee2e2', color: '#991b1b', borderRadius: '4px', padding: '1px 6px' }} title="Tylko Admin – niewidoczne dla asystentki">
                              <span role="img" aria-label="ukryte">🔒</span> Tylko Admin
                            </span>
                          )}
                          {effectiveAssistantEmails.map(assistantEmail => (
                            <span
                              key={`${item._id}-${assistantEmail}`}
                              style={{ fontSize: '10px', background: '#f5f3ff', color: '#7c3aed', borderRadius: '4px', padding: '1px 6px' }}
                              title={`Przydzielone: ${assistantEmail}`}
                            >
                              <i className="fas fa-user-shield" style={{ marginRight: '3px' }} />
                              {getAssistantDisplayName(assistantEmail)}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Comment button (top-right) */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTask(item);
                          dispatch(markTaskRead(playerId, item._id));
                        }}
                        style={{
                          background: 'none',
                          border: '1px solid #cbd5e1',
                          borderRadius: '4px',
                          color: '#2e496b',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '2px 8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0,
                        }}
                        title="Komentarze"
                      >
                        <i className="fas fa-comments" />
                        {item.comments && item.comments.filter(c => c.authorRole === 'athlete' && !c.readByOtherParty).length > 0 && (
                          <span
                            style={{
                              background: '#e53e3e',
                              color: '#fff',
                              borderRadius: '10px',
                              fontSize: '10px',
                              padding: '0 5px',
                              fontWeight: '700',
                              lineHeight: '16px',
                            }}
                          >
                            {item.comments.filter(c => c.authorRole === 'athlete' && !c.readByOtherParty).length}
                          </span>
                        )}
                      </button>

                      {/* Notes button (internal, admin/assistant only) */}
                      <button
                        type="button"
                        onClick={() => setNotesTask(item)}
                        style={{
                          background: 'none',
                          border: '1px solid #fde68a',
                          borderRadius: '4px',
                          color: '#d97706',
                          cursor: 'pointer',
                          fontSize: '12px',
                          padding: '2px 8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0,
                        }}
                        title="Notatki wewnętrzne"
                      >
                        <i className="fas fa-sticky-note" />
                        {item.internalNotes && item.internalNotes.length > 0 && (
                          <span
                            style={{
                              background: '#d97706',
                              color: '#fff',
                              borderRadius: '10px',
                              fontSize: '10px',
                              padding: '0 5px',
                              fontWeight: '700',
                              lineHeight: '16px',
                            }}
                          >
                            {item.internalNotes.length}
                          </span>
                        )}
                      </button>

                      {/* Delete (custom tasks only) */}
                      {!item.isDefault && (
                        <button
                          type="button"
                          onClick={() => dispatch(deleteCustomTask(playerId, item._id))}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#e53e3e',
                            cursor: 'pointer',
                            fontSize: '13px',
                            padding: '0 4px',
                            flexShrink: 0,
                          }}
                          title="Usuń zadanie"
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      )}
                    </div>

                    {/* === ROW 2: Admin controls === */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
                      marginLeft: '18px', marginBottom: (hasAttachments || hasLinks) ? '10px' : 0,
                    }}>
                      {/* Text value input (for taskType === 'text') */}
                      {item.taskType === 'text' && (
                        <input
                          type="text"
                          placeholder="Wpisz wartość..."
                          value={textValueDrafts[item._id] !== undefined ? textValueDrafts[item._id] : (item.textValue || '')}
                          onChange={e => {
                            const val = e.target.value;
                            setTextValueDrafts(prev => ({ ...prev, [item._id]: val }));
                          }}
                          onBlur={e => {
                            const val = e.target.value;
                            dispatch(updateTaskTextValue(playerId, item._id, val));
                            setTextValueDrafts(prev => { const next = { ...prev }; delete next[item._id]; return next; });
                          }}
                          style={{
                            flex: 2, minWidth: '180px', fontSize: '12px', padding: '4px 8px',
                            border: '1px solid #94a3b8', borderRadius: '4px', background: '#f8fafc',
                          }}
                        />
                      )}

                      <select
                        value={item.status}
                        onChange={e =>
                          dispatch(updateTaskAdmin(playerId, item._id, { status: e.target.value }))
                        }
                        style={{ ...selectCtrlStyle, minWidth: '130px' }}
                      >
                        {Object.entries(STATUS_LABELS).map(([val, lbl]) => (
                          <option key={val} value={val}>{lbl}</option>
                        ))}
                      </select>

                      <input
                        type="date"
                        value={formatDeadline(item.deadline)}
                        onChange={e =>
                          dispatch(updateTaskAdmin(playerId, item._id, { deadline: e.target.value || null }))
                        }
                        title="Termin wykonania"
                        style={selectCtrlStyle}
                      />

                      <select
                        value={item.priority || ''}
                        onChange={e =>
                          dispatch(updateTaskAdmin(playerId, item._id, { priority: e.target.value || null }))
                        }
                        title="Priorytet"
                        style={selectCtrlStyle}
                      >
                        {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>

                      <select
                        value={item.taskOwner || ''}
                        onChange={e =>
                          dispatch(updateTaskAdmin(playerId, item._id, { taskOwner: e.target.value || null }))
                        }
                        title="Właściciel zadania"
                        style={selectCtrlStyle}
                      >
                        {TASK_OWNER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>

                      {userRole === 'admin' && (
                        <button
                          type="button"
                          onClick={() =>
                            dispatch(updateTaskAdmin(playerId, item._id, { athleteVisible: !item.athleteVisible }))
                          }
                          title={item.athleteVisible ? 'Widoczne dla zawodnika' : 'Ukryte dla zawodnika'}
                          style={{
                            background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px',
                            color: item.athleteVisible ? '#2e496b' : '#92400e',
                            cursor: 'pointer', fontSize: '12px', padding: '3px 7px',
                          }}
                        >
                          <i className={`fas fa-eye${item.athleteVisible ? '' : '-slash'}`} />
                        </button>
                      )}

                      {userRole === 'admin' && (
                        <button
                          type="button"
                          onClick={() => setAssignModalTask(item)}
                          title={hasAssistantAssignmentVisual
                            ? `Przydzielone: ${effectiveAssistantEmails.join(', ')}`
                            : 'Przydziel asystentce'}
                          style={{
                            background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px',
                            color: hasAssistantAssignmentVisual ? '#7c3aed' : '#94a3b8',
                            cursor: 'pointer', fontSize: '12px', padding: '3px 7px',
                          }}
                        >
                          <i className="fas fa-user-shield" />
                        </button>
                      )}

                      {/* Requires toggles */}
                      <span style={{ display: 'inline-flex', gap: '2px', alignItems: 'center' }}>
                        {['file', 'text', 'link'].map(req => {
                          const active = Array.isArray(item.requires) && item.requires.includes(req);
                          const icons = { file: 'fa-paperclip', text: 'fa-pen', link: 'fa-link' };
                          return (
                            <button
                              key={req}
                              type="button"
                              onClick={() => {
                                const current = Array.isArray(item.requires) ? [...item.requires] : [];
                                const next = active ? current.filter(r => r !== req) : [...current, req];
                                dispatch(updateTaskAdmin(playerId, item._id, { requires: next }));
                              }}
                              title={`Wymaga: ${req === 'file' ? 'Plik' : req === 'text' ? 'Tekst' : 'Link'}`}
                              style={{
                                background: active ? '#2e496b' : '#f1f5f9',
                                color: active ? '#fff' : '#94a3b8',
                                border: 'none', borderRadius: '3px',
                                cursor: 'pointer', fontSize: '10px',
                                padding: '3px 5px', lineHeight: 1,
                              }}
                            >
                              <i className={`fas ${icons[req]}`} style={{ fontSize: '9px' }} />
                            </button>
                          );
                        })}
                      </span>
                    </div>

                    {/* === Attachments section === */}
                    {showFilesSection && (
                      <div style={{
                        marginLeft: '18px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        marginBottom: '6px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasAttachments ? '8px' : 0 }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            <i className="fas fa-paperclip" style={{ marginRight: '5px' }} />
                            Pliki {hasAttachments && <span style={{ fontWeight: 400 }}>({item.attachments.length})</span>}
                          </span>
                          <button
                            type="button"
                            onClick={() => setUploadModalTaskId(item._id)}
                            style={{
                              background: '#2e496b', color: '#fff', border: 'none',
                              borderRadius: '4px', cursor: 'pointer',
                              fontSize: '11px', padding: '3px 10px', fontWeight: 500,
                            }}
                          >
                            <i className="fas fa-plus" style={{ marginRight: '4px', fontSize: '9px' }} />
                            Dodaj plik
                          </button>
                        </div>
                        {hasAttachments && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {item.attachments.map(att => {
                              const isImage = att.contentType && att.contentType.startsWith('image/');
                              const isPdf = att.contentType === 'application/pdf';
                              const attUrl = getAttachmentDownloadUrl(playerId, item._id, att._id);
                              return (
                                <div
                                  key={att._id}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '10px',
                                    background: '#fff', borderRadius: '5px',
                                    border: '1px solid #e2e8f0', padding: '6px 10px',
                                  }}
                                >
                                  {/* File icon / thumbnail */}
                                  {isImage ? (
                                    <span
                                      style={{
                                        width: '32px', height: '32px', borderRadius: '4px',
                                        overflow: 'hidden', flexShrink: 0, cursor: 'pointer',
                                        border: '1px solid #e2e8f0',
                                      }}
                                      onClick={() => openAttachment(attUrl, att.originalName)}
                                      title="Podgląd"
                                    >
                                      <img
                                        src={attUrl} alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={e => { e.target.style.display = 'none'; }}
                                      />
                                    </span>
                                  ) : (
                                    <span style={{
                                      width: '32px', height: '32px', borderRadius: '4px', flexShrink: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      background: isPdf ? '#fef2f2' : '#f1f5f9',
                                    }}>
                                      <i className={isPdf ? 'fas fa-file-pdf' : 'fas fa-file'}
                                        style={{ color: isPdf ? '#dc2626' : '#64748b', fontSize: '14px' }} />
                                    </span>
                                  )}

                                  {/* File info */}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <button
                                      type="button"
                                      onClick={() => openAttachment(attUrl, att.originalName)}
                                      style={{
                                        background: 'none', border: 'none', padding: 0,
                                        color: '#2e496b', cursor: 'pointer', fontSize: '12px',
                                        fontWeight: 500, textDecoration: 'underline',
                                        textAlign: 'left', display: 'block',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                      }}
                                      title={att.originalName}
                                    >
                                      {att.originalName}
                                    </button>
                                    {att.description && (
                                      <div style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', marginTop: '1px' }} title={att.description}>
                                        {att.description.length > 40 ? att.description.slice(0, 40) + '...' : att.description}
                                      </div>
                                    )}
                                  </div>

                                  {/* Meta: date + uploader */}
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: '1px' }}>
                                    {att.createdAt && (
                                      <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                        {new Date(att.createdAt).toLocaleDateString('pl-PL')}
                                      </span>
                                    )}
                                    {att.uploadedBy && (
                                      <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }} title={att.uploadedBy}>
                                        {att.uploadedBy.split('@')[0]}
                                      </span>
                                    )}
                                  </div>

                                  {/* Delete */}
                                  <button
                                    type="button"
                                    onClick={() => dispatch(deleteAttachment(playerId, item._id, att._id))}
                                    style={{
                                      background: 'none', border: 'none', color: '#cbd5e1',
                                      cursor: 'pointer', fontSize: '12px', padding: '4px',
                                      flexShrink: 0, borderRadius: '4px',
                                    }}
                                    title="Usuń plik"
                                  >
                                    <i className="fas fa-trash-alt" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* === Links section === */}
                    {showLinksSection && (
                      <div style={{
                        marginLeft: '18px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '8px 12px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: hasLinks ? '8px' : 0, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px', marginRight: 'auto' }}>
                            <i className="fas fa-link" style={{ marginRight: '5px' }} />
                            Linki {hasLinks && <span style={{ fontWeight: 400 }}>({item.links.length})</span>}
                          </span>
                          <input
                            type="text"
                            placeholder="Wklej link..."
                            value={newLinkInputs[item._id] || ''}
                            onChange={e => {
                              const val = e.target.value;
                              setNewLinkInputs(prev => ({ ...prev, [item._id]: val }));
                            }}
                            onKeyDown={e => e.key === 'Enter' && handleAddLink(item._id)}
                            style={{
                              fontSize: '11px', padding: '3px 8px',
                              border: '1px solid #cbd5e1', borderRadius: '4px',
                              width: '200px', color: '#334155', background: '#fff',
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleAddLink(item._id)}
                            style={{
                              background: '#2e496b', color: '#fff', border: 'none',
                              borderRadius: '4px', cursor: 'pointer',
                              fontSize: '11px', padding: '3px 10px', fontWeight: 500,
                            }}
                          >
                            <i className="fas fa-plus" style={{ marginRight: '4px', fontSize: '9px' }} />
                            Dodaj
                          </button>
                        </div>
                        {hasLinks && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {item.links.map(lnk => (
                              <div
                                key={lnk._id}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  background: '#fff', borderRadius: '5px',
                                  border: '1px solid #e2e8f0', padding: '5px 10px',
                                }}
                              >
                                <i className="fas fa-external-link-alt" style={{ color: '#60a5fa', fontSize: '11px', flexShrink: 0 }} />
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <a
                                    href={lnk.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: '#1d4ed8', fontSize: '12px', display: 'block',
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                    }}
                                    title={lnk.url}
                                  >
                                    {displayLinkHost(lnk.url)}
                                  </a>
                                  <ChecklistLinkDate createdAt={lnk.createdAt} />
                                </span>
                                <button
                                  type="button"
                                  onClick={() => dispatch(deleteAdminLink(playerId, item._id, lnk._id))}
                                  style={{
                                    background: 'none', border: 'none', color: '#cbd5e1',
                                    cursor: 'pointer', fontSize: '12px', padding: '4px',
                                    flexShrink: 0,
                                  }}
                                  title="Usuń link"
                                >
                                  <i className="fas fa-trash-alt" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}

                {/* Add custom task — admin only */}
                {userRole !== 'assistant' && <div
                  style={{
                    padding: '10px 12px',
                    background: '#f8fafc',
                    borderTop: stepItems.length > 0 ? '1px solid #f1f5f9' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px', alignItems: 'flex-end' }}>
                    <input
                      type="text"
                      placeholder="Dodaj własne zadanie..."
                      value={newTaskLabel[step]}
                      onChange={e => {
                        const val = e.target.value;
                        setNewTaskLabel(prev => ({ ...prev, [step]: val }));
                      }}
                      onKeyDown={e => e.key === 'Enter' && handleAddTask(step)}
                      style={{
                        flex: 1,
                        minWidth: '160px',
                        fontSize: '13px',
                        padding: '5px 10px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                      }}
                    />
                    <input
                      type="date"
                      value={newTaskDeadline[step]}
                      onChange={e => {
                        const val = e.target.value;
                        setNewTaskDeadline(prev => ({ ...prev, [step]: val }));
                      }}
                      title="Termin (opcjonalnie)"
                      style={{
                        fontSize: '13px',
                        padding: '5px 8px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                      }}
                    />
                    <select
                      value={newTaskPriority[step]}
                      onChange={e => { const val = e.target.value; setNewTaskPriority(prev => ({ ...prev, [step]: val })); }}
                      title="Priorytet (opcjonalnie)"
                      style={{ fontSize: '12px', padding: '5px 6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                    >
                      {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select
                      value={newTaskOwner[step]}
                      onChange={e => { const val = e.target.value; setNewTaskOwner(prev => ({ ...prev, [step]: val })); }}
                      title="Właściciel (opcjonalnie)"
                      style={{ fontSize: '12px', padding: '5px 6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                    >
                      {TASK_OWNER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={() => handleAddTask(step)}
                      disabled={!newTaskLabel[step].trim()}
                      style={{
                        background: '#2e496b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '5px 14px',
                        fontSize: '13px',
                        cursor: newTaskLabel[step].trim() ? 'pointer' : 'not-allowed',
                        opacity: newTaskLabel[step].trim() ? 1 : 0.5,
                      }}
                    >
                      <i className="fas fa-plus" /> Dodaj
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={newTaskAthleteVisible[step]}
                        onChange={e => {
                          const checked = e.target.checked;
                          setNewTaskAthleteVisible(prev => ({ ...prev, [step]: checked }));
                        }}
                      />
                      Widoczne dla zawodnika
                    </label>
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Wymaga:</span>
                    {[{ val: 'file', lbl: 'Plik' }, { val: 'text', lbl: 'Tekst' }, { val: 'link', lbl: 'Link' }].map(({ val, lbl }) => (
                      <label key={val} style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={newTaskRequires[step].includes(val)}
                          onChange={e => {
                            const checked = e.target.checked;
                            setNewTaskRequires(prev => ({
                              ...prev,
                              [step]: checked
                                ? [...prev[step], val]
                                : prev[step].filter(r => r !== val),
                            }));
                          }}
                        />
                        {lbl}
                      </label>
                    ))}
                  </div>
                </div>}
              </div>
            </div>
          );
        })
      )}

      {/* Comment panel */}
      {selectedTask && (
        <TaskCommentPanel
          task={selectedTask}
          playerId={playerId}
          userRole="admin"
          onClose={() => {
            setSelectedTask(null);
            if (new URLSearchParams(location.search).has('task')) {
              history.replace(location.pathname);
            }
          }}
        />
      )}

      {/* Notes panel */}
      {notesTask && (
        <TaskNotesPanel
          task={notesTask}
          playerId={playerId}
          onClose={() => setNotesTask(null)}
        />
      )}

      <FileUploadModal
        show={!!uploadModalTaskId}
        onClose={() => setUploadModalTaskId(null)}
        onUpload={(file, desc) => {
          dispatch(uploadAttachment(playerId, uploadModalTaskId, file, desc || undefined));
          setUploadModalTaskId(null);
        }}
      />

      {/* Assistant assignment modal */}
      {assignModalTask && ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          onClick={() => setAssignModalTask(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9000,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
              width: '100%', maxWidth: '420px',
              padding: '28px 28px 20px',
            }}
          >
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '6px' }}>
              <h4 style={{ margin: 0, color: '#1e293b', fontWeight: '700', fontSize: '17px' }}>
                <i className="fas fa-user-shield" style={{ marginRight: '8px', color: '#7c3aed' }} />
                Przydziel asystentce
              </h4>
              <button
                type="button"
                onClick={() => setAssignModalTask(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px', lineHeight: 1, padding: '0 0 0 8px' }}
                aria-label="Zamknij"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Task label */}
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#64748b', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
              Zadanie: <strong style={{ color: '#334155' }}>{assignModalTask.label}</strong>
            </p>

            {/* Currently assigned info */}
            {assignModalTask.assignedToAssistant && (
              <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#6d28d9' }}>
                <i className="fas fa-check-circle" style={{ marginRight: '6px' }} />
                Aktualnie przydzielone: <strong>{getAssistantDisplayName(assignModalTask.assignedToAssistant)}</strong>
              </div>
            )}
            {!assignModalTask.assignedToAssistant && singleFullAccessAssistantEmail && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#1d4ed8' }}>
                <i className="fas fa-layer-group" style={{ marginRight: '6px' }} />
                Zadanie jest widoczne dla tej asystentki przez <strong>Pełny dostęp</strong>.
              </div>
            )}
            {taskExcludedFromSingleFullAccess && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#b91c1c' }}>
                <i className="fas fa-user-slash" style={{ marginRight: '6px' }} />
                To zadanie zostało odpięte od pełnego dostępu asystentki.
              </div>
            )}

            {/* Assistant list */}
            {assistants.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px', padding: '12px 0' }}>
                Brak asystentek w systemie.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {assistants.map(a => {
                  const hasFullAccess = fullAccessAssistants.some(f => f.assistantEmail === a.universityEmail);
                  const isExcluded = Array.isArray(assignModalTask.excludedAssistantEmails) && assignModalTask.excludedAssistantEmails.includes(a.universityEmail);
                  const isSelected = assignModalTask.assignedToAssistant === a.universityEmail || (hasFullAccess && !isExcluded);
                  const fullName = [a.firstName, a.lastName].filter(Boolean).join(' ') || a.universityEmail;
                  return (
                    <button
                      key={a.universityEmail}
                      type="button"
                      onClick={() => {
                        dispatch(assignTaskToAssistant(playerId, assignModalTask._id, a.universityEmail));
                        setAssignModalTask(null);
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '12px 14px', borderRadius: '8px', cursor: 'pointer',
                        border: isSelected ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                        background: isSelected ? '#f5f3ff' : '#f8fafc',
                        textAlign: 'left', width: '100%',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                    >
                      <div style={{
                        width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                        background: isSelected ? '#7c3aed' : '#e2e8f0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: isSelected ? '#fff' : '#64748b', fontSize: '14px', fontWeight: '700',
                      }}>
                        {(a.firstName || a.universityEmail || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: isSelected ? '#6d28d9' : '#1e293b', fontSize: '14px' }}>
                          {fullName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                          {a.universityEmail}
                          {hasFullAccess && !isExcluded && ' • pełny dostęp'}
                          {isExcluded && ' • odpięte z pełnego dostępu'}
                        </div>
                      </div>
                      {isSelected && (
                        <i className="fas fa-check-circle" style={{ marginLeft: 'auto', color: '#7c3aed', fontSize: '16px' }} />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Footer buttons */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
              {!assignModalTask.assignedToAssistant && singleFullAccessAssistantEmail && !taskExcludedFromSingleFullAccess && (
                <button
                  type="button"
                  onClick={() => {
                    dispatch(updateTaskAdmin(playerId, assignModalTask._id, { excludeForAssistantEmail: singleFullAccessAssistantEmail }));
                    setAssignModalTask(null);
                  }}
                  style={{
                    background: 'none', border: '1px solid #fca5a5', borderRadius: '6px',
                    color: '#dc2626', padding: '7px 16px', fontSize: '13px', cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-user-slash" style={{ marginRight: '5px' }} />
                  Odepnij to zadanie
                </button>
              )}
              {!assignModalTask.assignedToAssistant && singleFullAccessAssistantEmail && taskExcludedFromSingleFullAccess && (
                <button
                  type="button"
                  onClick={() => {
                    dispatch(updateTaskAdmin(playerId, assignModalTask._id, { includeForAssistantEmail: singleFullAccessAssistantEmail }));
                    setAssignModalTask(null);
                  }}
                  style={{
                    background: 'none', border: '1px solid #86efac', borderRadius: '6px',
                    color: '#15803d', padding: '7px 16px', fontSize: '13px', cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-user-check" style={{ marginRight: '5px' }} />
                  Przywróć zadanie
                </button>
              )}
              {assignModalTask.assignedToAssistant && (
                <button
                  type="button"
                  onClick={() => {
                    dispatch(assignTaskToAssistant(playerId, assignModalTask._id, null));
                    setAssignModalTask(null);
                  }}
                  style={{
                    background: 'none', border: '1px solid #fca5a5', borderRadius: '6px',
                    color: '#dc2626', padding: '7px 16px', fontSize: '13px', cursor: 'pointer',
                  }}
                >
                  <i className="fas fa-times" style={{ marginRight: '5px' }} />
                  Cofnij przypisanie
                </button>
              )}
              <button
                type="button"
                onClick={() => setAssignModalTask(null)}
                style={{
                  background: '#f1f5f9', border: 'none', borderRadius: '6px',
                  color: '#475569', padding: '7px 16px', fontSize: '13px', cursor: 'pointer',
                }}
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminChecklist;
